import { useContext, useMemo, useRef } from 'react'
import { Box } from '@mui/material'
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro'

import useStickyHeaders from '../../../../utils/hooks/useStickyHeaders'
import AlertsTableEnterExitTime from '../../../devices/components/alert-table-column-renderers/alerts-table-enter-exit-time'
import AlertTableMultilineText from '../../../devices/components/alert-table-column-renderers/alerts-table-multiline-text'
import AlertsTableResponse from '../../../devices/components/alert-table-column-renderers/alerts-table-response'
import AlertTableSeverity, { alertTableSeverityComparator } from '../../../devices/components/alert-table-column-renderers/alerts-table-severity'
import AlertsTablePanelContent from '../../../devices/components/alerts-table-panel-content'
import AccountsContext from '../../context'
import useAccountStyles from '../../index.styles'

import AlertsTableDevice from './alert-table-device'

const columns: GridColDef<AccountAlertTableRow>[] = [
  {
    field: 'severity',
    headerName: 'Severity',
    editable: false,
    sortable: true,
    flex: 1,
    renderCell: AlertTableSeverity,
    sortComparator: alertTableSeverityComparator,
  },
  {
    field: 'name',
    headerName: 'Alert Name',
    editable: false,
    sortable: true,
    flex: 2,
    renderCell: AlertTableMultilineText,
  },
  {
    field: 'deviceInfo',
    headerName: 'Device',
    editable: false,
    sortable: true,
    flex: 2.5,
    sortComparator: (item1, item2) => item1.deviceName?.localeCompare(item2.deviceName),
    renderCell: AlertsTableDevice,
  },
  {
    field: 'entryTime',
    headerName: 'Entry Time',
    editable: false,
    sortable: true,
    flex: 1,
    renderCell: AlertsTableEnterExitTime,
  },
  {
    field: 'exitTime',
    headerName: 'Exit Time',
    editable: false,
    sortable: true,
    flex: 1,
    renderCell: AlertsTableEnterExitTime,
  },
  {
    field: 'response',
    headerName: 'Response',
    editable: false,
    sortable: true,
    flex: 1.5,
    renderCell: AlertsTableResponse,
  },
] satisfies GridColDef<AccountAlertTableRow>[]

const AlertsTabPanel = () => {
  const accountClasses = useAccountStyles

  const { searchText, alerts, isLoading, isLoadingAlerts } = useContext(AccountsContext)
  const containerRef = useRef(null)
  useStickyHeaders({ containerRef })

  const rows: AccountAlertTableRow[] = useMemo(() => {
    let tableData = alerts
    if (searchText) {
      tableData = alerts.filter(
        (item) =>
          item.ruleName.toLowerCase().includes(searchText.toLowerCase()) ||
          item.deviceName.toLowerCase().includes(searchText.toLowerCase()) ||
          item.deviceId.toString().toLowerCase().includes(searchText.toLowerCase())
      )
    }

    return tableData.map((alert) => {
      let severity: 'urgent' | 'warning' | 'info' | 'unknown' = 'unknown'
      if (alert.tags.includes('info')) severity = 'info'
      if (alert.tags.includes('warning')) severity = 'warning'
      if (alert.tags.includes('urgent')) severity = 'urgent'

      const deviceInfo: IDeviceInfo = {
        deviceName: alert.deviceName,
        deviceId: alert.deviceId.toString(),
        deviceLocation: alert.deviceLocation,
      }

      const sortedEvents = [...alert.events].sort((a, b) => new Date(b.updatedAt).valueOf() - new Date(a.updatedAt).valueOf())
      const entryTimeEvent = sortedEvents.find((event) => event.event === 'enter')
      const exitTimeEvent = sortedEvents.find((event) => event.event === 'exit')

      const sortedResponses = [...alert.responses].sort((a, b) => new Date(b.updatedAt).valueOf() - new Date(a.updatedAt).valueOf())
      const bestResponse =
        sortedResponses.find((response) => response.note === 'ack') ??
        sortedResponses.find((response) => response.note === 'decline') ??
        sortedResponses.find((response) => response.note === 'test')

      return {
        id: alert.alertId,
        severity,
        name: alert.ruleName,
        deviceInfo,
        entryTime: entryTimeEvent?.updatedAt,
        exitTime: exitTimeEvent?.updatedAt,
        response: bestResponse as IAlertResponseHistory,
        alert: alert as IFolderAlertExtended,
      } satisfies AccountAlertTableRow
    })
  }, [alerts, searchText])

  return (
    <Box sx={accountClasses.dataGridContainer}>
      <DataGridPro
        ref={containerRef}
        columns={columns}
        rows={rows}
        loading={isLoading || isLoadingAlerts}
        rowHeight={80}
        getDetailPanelContent={(params) => (params.row.alert.responses.length > 0 ? <AlertsTablePanelContent {...params} /> : undefined)}
        getDetailPanelHeight={() => 'auto'}
        autosizeOptions={{ expand: true }}
        pagination={true}
        pageSizeOptions={[5, 10, 20, 100]}
        disableRowSelectionOnClick={true}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
      />
    </Box>
  )
}

export default AlertsTabPanel
